import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { respondConnection, getUser, getUserConnections } from "../../api/api";
import { defaultPfpUrl } from "../utils";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from "../ui/button";
import { useUserRealtime } from "../../context/UserRealtimeContext";
import {
  NotificationTabHeader,
  NotificationEmptyState,
  NotificationErrorPanel,
  NotificationRequestCard,
} from "./NotificationTabShared";

const pendingKey = (list) => list.map((c) => c.id).join(",");

/**
 * Pending connection requests (receiver is the current user).
 * @param {Object} props
 * @param {number} props.userId
 * @param {Array} props.connections
 * @param {function} props.setConnections
 * @param {function} [props.refreshUser]
 * @param {function} [props.onPendingConnectionsChange]
 * @returns {JSX.Element}
 */
function ConnectionsTab({
  userId,
  connections,
  setConnections,
  refreshUser,
  onPendingConnectionsChange,
}) {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [senderUsers, setSenderUsers] = useState({});
  const { reportConnectionChange } = useUserRealtime();

  const pendingConnections = useMemo(
    () => connections?.filter((conn) => conn.status === "pending" && conn.receiver_id === userId) || [],
    [connections, userId]
  );

  const key = useMemo(() => pendingKey(pendingConnections), [pendingConnections]);

  useEffect(() => {
    const fetchSenderUsers = async () => {
      if (!userId || pendingConnections.length === 0) {
        return;
      }

      const userPromises = pendingConnections.map(async (conn) => {
        const senderId = conn.sender_id;
        try {
          const result = await getUser(userId, senderId);
          if (result.success && result.data) {
            return { senderId, user: result.data };
          }
        } catch (err) {
          console.error("Failed to fetch user:", senderId, err);
        }
        return null;
      });

      const results = await Promise.all(userPromises);
      setSenderUsers((prev) => {
        const next = { ...prev };
        results.forEach((result) => {
          if (result) {
            next[result.senderId] = result.user;
          }
        });
        return next;
      });
    };

    fetchSenderUsers();
  }, [userId, key]);

  /**
   * @param {number} connectionId
   * @param {string} status
   * @returns {Promise<void>}
   */
  const handleRespond = async (connectionId, status) => {
    setLoading(true);
    setError(null);
    try {
      const result = await respondConnection({
        connection_id: connectionId,
        user_id: userId,
        status,
      });

      if (result.success) {
        const responded = connections.find((conn) => conn.id === connectionId);
        reportConnectionChange({
          peerUserId: responded?.sender_id,
          status: status === "active" ? "active" : "rejected",
          connectionId,
        });
        const connectionsResult = await getUserConnections(userId);
        if (connectionsResult.success && connectionsResult.data) {
          setConnections(connectionsResult.data);
        } else {
          setConnections(connections.filter((conn) => conn.id !== connectionId));
        }
        if (typeof onPendingConnectionsChange === "function") {
          await onPendingConnectionsChange();
        }
        if (refreshUser) {
          await refreshUser();
        }
      } else {
        setError(result.error || "Failed to respond to connection");
      }
    } catch (err) {
      setError("Network error while responding to connection");
    } finally {
      setLoading(false);
    }
  };

  const header = (
    <NotificationTabHeader
      title="Pending connections"
      description="Accept or decline requests from other users."
    />
  );

  if (error) {
    return (
      <div>
        {header}
        <NotificationErrorPanel message={error} />
      </div>
    );
  }

  if (pendingConnections.length === 0) {
    return (
      <div>
        {header}
        <NotificationEmptyState
          title="No pending connection requests"
          subtitle="You are all caught up."
        />
      </div>
    );
  }

  return (
    <div>
      {header}
      <ul className="list-none space-y-4 p-0">
        {pendingConnections.map((connection) => {
          const senderUser = senderUsers[connection.sender_id];
          const senderName = senderUser
            ? `${senderUser.first_name || ""} ${senderUser.last_name || ""}`.trim() || senderUser.username
            : "Unknown user";
          const senderPfp = senderUser?.pfp_url || defaultPfpUrl;
          const initial = (senderName || "U")
            .split(/\s+/)
            .filter(Boolean)
            .map((w) => w[0])
            .join("")
            .slice(0, 2)
            .toUpperCase();

          return (
            <li key={connection.id}>
              <NotificationRequestCard>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex flex-1 flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
                    <Avatar className="h-16 w-16 shrink-0 border-2 border-ui-border">
                      <AvatarImage
                        src={senderPfp}
                        alt=""
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = defaultPfpUrl;
                        }}
                      />
                      <AvatarFallback className="text-text-secondary">{initial}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <Link
                        to={`/profile/${connection.sender_id}`}
                        className="text-lg font-semibold text-accent transition-colors hover:text-text-accent-hover"
                      >
                        {senderName}
                      </Link>
                      {senderUser?.username ? (
                        <p className="mt-0.5 text-sm text-text-muted">@{senderUser.username}</p>
                      ) : null}
                      {connection.created_at ? (
                        <p className="mt-2 text-sm text-text-muted">
                          Requested {new Date(connection.created_at).toLocaleDateString()}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:justify-end">
                    <Button
                      type="button"
                      onClick={() => handleRespond(connection.id, "active")}
                      disabled={loading}
                    >
                      Accept
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleRespond(connection.id, "rejected")}
                      disabled={loading}
                    >
                      Decline
                    </Button>
                  </div>
                </div>
              </NotificationRequestCard>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default ConnectionsTab;
